import { useTicketSla } from '@servicetim/ui/api';
import { Badge } from '@servicetim/ui/components';
import { formatDateTime } from './format';
import { RailCard, RailRow } from './rail-card';
import { useTicketContext } from './ticket-context';

const STATE_VARIANT = {
  running: 'info',
  paused: 'secondary',
  completed: 'success',
  breached: 'destructive',
  cancelled: 'secondary',
} as const;

const KIND_LABEL = { response: 'Response', resolution: 'Resolution' } as const;

function remaining(ms: number | null): string {
  if (ms === null) return '—';
  const abs = Math.abs(ms);
  const minutes = Math.round(abs / 60_000);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const label = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  return ms < 0 ? `${label} overdue` : `${label} left`;
}

/** SLA timers on this ticket (`GET /v1/tickets/{id}/sla`, S-07-08): one row per policy × kind. */
export function SlaCard() {
  const { ticket } = useTicketContext();
  const sla = useTicketSla(ticket.id);
  const items = sla.data?.items ?? [];

  if (sla.isPending || items.length === 0) return null;

  return (
    <RailCard title="SLA">
      <ul className="space-y-3">
        {items.map((row) => (
          <li key={row.id} className="space-y-1">
            <RailRow label={KIND_LABEL[row.kind]}>
              <Badge variant={STATE_VARIANT[row.state]}>{row.state}</Badge>
            </RailRow>
            {row.state === 'running' || row.state === 'paused' ? (
              <RailRow label="Remaining">
                <span
                  className={
                    row.remainingMs !== null && row.remainingMs < 0
                      ? 'text-red-700 dark:text-red-300'
                      : undefined
                  }
                >
                  {remaining(row.remainingMs)}
                </span>
              </RailRow>
            ) : null}
            {row.dueAt ? (
              <RailRow label="Due">
                <span className="text-fg-muted text-xs">{formatDateTime(row.dueAt)}</span>
              </RailRow>
            ) : null}
          </li>
        ))}
      </ul>
    </RailCard>
  );
}
